import { useState, useEffect, useCallback, useRef } from 'react'
import { competitorAnalysisService } from '@/services/competitor-analysis'
import type { CompAnalysisJob } from '@/services/competitor-analysis/types'
import apiClient from '@/shared/lib/api'
import CompetitorAnalysisPipeline from './CompetitorAnalysisPipeline'
import CompetitorAnalysisResults from './CompetitorAnalysisResults'
import { Search, Loader2, RefreshCw, Building2, AlertCircle } from 'lucide-react'

interface BrandInfo {
  businessName: string
  website: string
}

export default function CompetitorAnalysisPage() {
  const [brandInfo, setBrandInfo] = useState<BrandInfo | null>(null)
  const [currentJob, setCurrentJob] = useState<CompAnalysisJob | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Load brand info and check for existing analysis on mount
  useEffect(() => {
    loadInitialData()
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  // Poll for job updates when running
  useEffect(() => {
    if (currentJob && (currentJob.status === 'running' || currentJob.status === 'pending')) {
      pollRef.current = setInterval(() => {
        pollJobStatus(currentJob.job_id)
      }, 3000)

      return () => {
        if (pollRef.current) clearInterval(pollRef.current)
      }
    }

    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [currentJob?.job_id, currentJob?.status])

  const loadInitialData = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Fetch company profile to get brand info
      const companyResponse = await apiClient.get('/my-company')
      const profile = companyResponse.data?.profile
      const overview = profile?.overview

      if (overview?.businessName || overview?.website) {
        setBrandInfo({
          businessName: overview.businessName || '',
          website: overview.website || '',
        })
      }

      // Check for existing analysis jobs
      const jobsResponse = await competitorAnalysisService.listJobs()
      const jobs = jobsResponse.jobs || []

      if (jobs.length > 0) {
        // Get the most recent job
        const latestJob = jobs[0]

        // If it's completed, fetch full details with result
        if (latestJob.status === 'completed') {
          const fullJob = await competitorAnalysisService.getJob(latestJob.job_id)
          setCurrentJob(fullJob)
        } else {
          setCurrentJob(latestJob)
        }
      }
    } catch (err) {
      console.error('[CompetitorAnalysis] Failed to load initial data:', err)
      setError('Failed to load data. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const pollJobStatus = useCallback(async (jobId: string) => {
    try {
      const job = await competitorAnalysisService.getJob(jobId)
      setCurrentJob(job)

      // Stop polling on terminal states
      if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
        if (pollRef.current) clearInterval(pollRef.current)
      }
    } catch (err) {
      console.error('[CompetitorAnalysis] Poll error:', err)
    }
  }, [])

  const handleRunAnalysis = async () => {
    if (!brandInfo?.website && !brandInfo?.businessName) {
      setError('Please configure your brand in Brand Context Engineering first.')
      return
    }

    setIsCreating(true)
    setError(null)

    try {
      const job = await competitorAnalysisService.createJob({
        brand_url: brandInfo.website || '',
        brand_name: brandInfo.businessName || undefined,
      })
      setCurrentJob(job)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start analysis')
    } finally {
      setIsCreating(false)
    }
  }

  const handleReanalyze = async () => {
    setCurrentJob(null)
    await handleRunAnalysis()
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-purple-500 animate-spin mx-auto mb-3" />
          <p className="text-sm text-tf-text-muted">Loading competitor analysis...</p>
        </div>
      </div>
    )
  }

  // No brand configured
  if (!brandInfo?.businessName && !brandInfo?.website) {
    return (
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-amber-500/10 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-xl font-bold text-tf-text mb-2">Brand Not Configured</h2>
          <p className="text-sm text-tf-text-muted mb-4">
            Please configure your brand information in Brand Context Engineering before running competitor analysis.
          </p>
          <a
            href="/marketing/debug/brand-context"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500 text-white hover:bg-purple-600 transition-colors text-sm font-medium"
          >
            <Building2 className="w-4 h-4" />
            Configure Brand
          </a>
        </div>
      </div>
    )
  }

  // Show pipeline if job is running
  if (currentJob && (currentJob.status === 'running' || currentJob.status === 'pending')) {
    return <CompetitorAnalysisPipeline job={currentJob} />
  }

  // Show results if job is completed
  if (currentJob?.status === 'completed' && currentJob.result) {
    return (
      <CompetitorAnalysisResults
        job={currentJob}
        onBack={() => {}} // No-op since we don't navigate away
        onReanalyze={handleReanalyze}
      />
    )
  }

  // Show start analysis view
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 max-w-2xl mx-auto w-full">
      {/* Error toast */}
      {error && (
        <div className="w-full mb-6 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-500">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Hero */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
          <Search className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-tf-text mb-2">Competitor Analysis</h1>
        <p className="text-sm text-tf-text-muted max-w-md">
          Automatically discover and analyze your competitors.
          Results will be saved to your brand context.
        </p>
      </div>

      {/* Brand Info Card */}
      <div className="w-full mb-6 p-4 rounded-xl bg-tf-bg-card border border-tf-border">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <p className="text-sm font-medium text-tf-text">
              {brandInfo.businessName || 'Your Brand'}
            </p>
            {brandInfo.website && (
              <p className="text-xs text-tf-text-muted">{brandInfo.website}</p>
            )}
          </div>
        </div>
        <p className="text-xs text-tf-text-muted">
          Analysis will be performed for this brand from your company profile.
        </p>
      </div>

      {/* Previous Analysis Info */}
      {currentJob?.status === 'failed' && (
        <div className="w-full mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20">
          <p className="text-sm text-red-500">
            Previous analysis failed: {currentJob.error_message || 'Unknown error'}
          </p>
        </div>
      )}

      {/* Action Button */}
      <button
        onClick={handleRunAnalysis}
        disabled={isCreating}
        className="w-full max-w-md py-4 rounded-xl font-medium text-white bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 shadow-lg shadow-purple-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isCreating ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Starting Analysis...
          </span>
        ) : currentJob ? (
          <span className="flex items-center justify-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Re-analyze Competitors
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <Search className="w-5 h-5" />
            Analyze Competitors
          </span>
        )}
      </button>

      {/* Last analysis info */}
      {currentJob?.completed_at && (
        <p className="mt-4 text-xs text-tf-text-muted">
          Last analyzed: {new Date(currentJob.completed_at).toLocaleDateString()} at{' '}
          {new Date(currentJob.completed_at).toLocaleTimeString()}
        </p>
      )}
    </div>
  )
}
